import { useCallback, useEffect, useState } from 'react';

import { useAddToast } from '../../../../contexts/ToastContext';
import { useIsOrgScanning, useOrgScanAll } from '../../../../hooks/api';

/**
 * This is a small wrapper around the logic for scanning all projects,
 * and determining if there is currently an OrgScanAll job running.
 *
 * The status is cached in Redis, so after we make the orgScanAll request,
 * there the next `/scanning` response will return false (because the job needs
 * to be picked up by Faktory which can take a second). This patches for that.
 *
 * Finally, this hook will show a toast when the scanning is done. This is because
 * we can't really use the JobNotificationHub, because the OrgScanAll job technically
 * will be "done", but the /scanning endpoint will correctly return `true` until all of the
 * individual project scans are done.
 */
export default function useOrgScanning() {
  const addToast = useAddToast();
  const [isCurrentlyScanning, setIsCurrentlyScanning] = useState(false);

  //  Check if we are currently scanning all projects
  const { data: isOrgScanningResponse } = useIsOrgScanning(isCurrentlyScanning);
  //  Get the mutation function for scanning all projects
  const { mutate: orgScanAll } = useOrgScanAll();

  useEffect(() => {
    if (!!isOrgScanningResponse?.scanning && !isCurrentlyScanning) {
      setIsCurrentlyScanning(true);
    } else if (!isOrgScanningResponse?.scanning && isCurrentlyScanning) {
      setIsCurrentlyScanning(false);
      //  Since this is going from "we were not scanning" to "we are scanning",
      addToast({
        type: 'success',
        content: 'Done scanning all projects',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect when the response changes
  }, [isOrgScanningResponse, addToast]);

  const onOrgScanAll = useCallback(async () => {
    await orgScanAll();
    setIsCurrentlyScanning(true);
  }, [orgScanAll]);

  return {
    isCurrentlyScanning,
    onOrgScanAll,
  };
}
